/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'
import {
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Stack,
    Text,
    useDisclosure
} from '@chakra-ui/react'
import useDoubleOptIn from './use-double-opt-in'
import LoginForm from '../components/login'
import ResetPasswordForm from '../components/reset-password'
import RegisterForm from '../components/register'
import PasswordlessEmailConfirmation from '../components/email-confirmation/index'
import {noop} from '../utils/utils'
import useRegistrationErrorMessage from './use-error-message'
import {useWishList, useMergeWishlistMutation} from './use-wish-list'
import useOcapiSite from './useOcapiSite'
import {
    ACCOUNT_HREF,
    API_ERROR_MESSAGE,
    CREATE_ACCOUNT_FIRST_ERROR_MESSAGE,
    FEATURE_UNAVAILABLE_ERROR_MESSAGE,
    LOGIN_TYPES,
    PASSWORDLESS_ERROR_MESSAGES,
    PWA_RESET_PASSWORD_ENABLED,
    TOAST_MESSAGE_MERGE_WISHLIST,
    TRACKING_TIMEOUT,
    USER_NOT_FOUND_ERROR
} from '../constants'
import {useToast} from './use-toast'
import useNavigation from './use-navigation'
import useEffectEvent from './use-effect-event'
import {trackEvent} from '../tracking/analytics'
import {
    AuthHelpers,
    ShopperCustomersMutations,
    useAuthHelper,
    useCustomer,
    useCustomerId,
    useCustomerType,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import {useOcapiPasswordResetMutation} from './useOcapi'
import {useCustomerNewsletter} from './useScapiCustomApi'
import Link from '../components/link'
import {useCurrentBasket} from './use-current-basket'
import {useAppOrigin} from './use-app-origin'
import {usePasswordReset} from './use-password-reset'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {isAbsoluteURL} from '../page-designer/utils'
import {useCurrentCustomer} from './use-current-customer'
import useLenisScrollLock from './use-lenis-scroll-lock'
import useMultiSite from '../hooks/use-multi-site'

export const LOGIN_VIEW = 'login'
export const REGISTER_VIEW = 'register'
export const PASSWORD_VIEW = 'password'
export const EMAIL_VIEW = 'email'

export const AuthModal = ({
    initialView = LOGIN_VIEW,
    initialEmail = '',
    onLoginSuccess = noop,
    onRegistrationSuccess = noop,
    onPasswordResetSuccess = noop,
    isPasswordlessEnabled = false,
    isSocialEnabled = false,
    idps = [],
    ...props
}) => {
    const {formatMessage} = useIntl()
    const {isDoubleOptInEnabled} = useDoubleOptIn()
    const customerId = useCustomerId()
    const {isRegistered} = useCustomerType()
    const errorMsg = useRegistrationErrorMessage()
    const {locale} = useMultiSite()
    // TODO v3: check if this is needed
    // const prevAuthType = usePrevious(customerType)

    const customer = useCustomer(
        {parameters: {customerId}},
        {enabled: !!customerId && isRegistered}
    )

    const navigate = useNavigation()
    const [currentView, setCurrentView] = useState(initialView)
    const form = useForm()
    const submittedEmail = useRef()
    const closeButtonRef = useRef()
    const toast = useToast()
    const login = useAuthHelper(AuthHelpers.LoginRegisteredUserB2C)
    const registerWithLogin = useAuthHelper(AuthHelpers.Register)
    const appOrigin = useAppOrigin()
    const registerWithoutLogin = useShopperCustomersMutation(
        ShopperCustomersMutations.RegisterCustomer
    )
    const {mutateAsync: subscribeToNewsletter} = useCustomerNewsletter()
    const passwordReset = useOcapiPasswordResetMutation()
    const {wishlistEnabled} = useOcapiSite().preferences
    const {isD2BCustomer} = useCurrentCustomer().data

    const {data: basket, isLoading: isBasketLoading} = useCurrentBasket()

    const [loginType, setLoginType] = useState(LOGIN_TYPES.PASSWORD)
    const [passwordlessLoginEmail, setPasswordlessLoginEmail] = useState(initialEmail)
    const {getPasswordResetToken} = usePasswordReset()
    const authorizePasswordlessLogin = useAuthHelper(AuthHelpers.AuthorizePasswordless)
    const passwordlessConfigCallback = getConfig().app.login?.passwordless?.callbackURI
    const callbackURL = isAbsoluteURL(passwordlessConfigCallback)
        ? passwordlessConfigCallback
        : `${appOrigin}${passwordlessConfigCallback}`

    const {mutate: mergeWishlistMutation} = useMergeWishlistMutation(
        {enabled: !!wishlistEnabled},
        {
            onSettled: (data) => {
                if (data) {
                    toast({
                        title: `${formatMessage(TOAST_MESSAGE_MERGE_WISHLIST)}`
                    })
                }
            }
        }
    )
    const {data: wishlist} = useWishList({enabled: !!wishlistEnabled})

    const wishlistProductIds = useMemo(
        () =>
            wishlistEnabled
                ? wishlist?.customerProductListItems?.map((item) => item.productId)
                : [],
        [wishlistEnabled, wishlist?.customerProductListItems]
    )

    // TODO v3: check if this is needed
    // const {data: baskets} = useCustomerBaskets(
    //   {parameters: {customerId}},
    //   {enabled: !!customerId && !isServer, keepPreviousData: true}
    // )
    // const mergeBasket = useShopperBasketsMutation('mergeBasket')

    const submitForm = useEffectEvent(async (data) => {
        form.clearErrors()

        const handlePasswordlessLogin = async (email) => {
            try {
                const redirectPath = window.location.pathname + window.location.search
                await authorizePasswordlessLogin.mutateAsync({
                    userid: email,
                    callbackURI: `${callbackURL}?redirectUrl=${redirectPath}`
                })
                setCurrentView(EMAIL_VIEW)
            } catch (error) {
                const message = USER_NOT_FOUND_ERROR.test(error.message)
                    ? formatMessage(CREATE_ACCOUNT_FIRST_ERROR_MESSAGE)
                    : PASSWORDLESS_ERROR_MESSAGES.some((msg) => msg.test(error.message))
                    ? formatMessage(FEATURE_UNAVAILABLE_ERROR_MESSAGE)
                    : formatMessage(API_ERROR_MESSAGE)
                form.setError('global', {type: 'manual', message})
            }
        }

        return {
            login: async (data) => {
                if (loginType === LOGIN_TYPES.PASSWORD) {
                    const loginPromise = login.mutateAsync({
                        username: data.email,
                        password: data.password
                    })
                    const trackEventPromise = Promise.race([
                        trackEvent({event: 'login', email: data.email}),
                        // wait for the tracking timeout and continue regardless of the tracking result -
                        // this can happen if the page does not include useInitialDataLayer hook
                        new Promise((resolve) => setTimeout(resolve, TRACKING_TIMEOUT))
                    ])
                    // wait for tracking and login to complete, as otherwise the user might be redirected before the tracking is sent
                    await Promise.allSettled([loginPromise, trackEventPromise])

                    // TODO v3: check if this is needed
                    // const hasBasketItem = baskets?.baskets?.[0]?.productItems?.length > 0
                    // // we only want to merge basket when the user is logged in as a recurring user
                    // // only recurring users trigger the login mutation, new user triggers register mutation
                    // // this logic needs to stay in this block because this is the only place that tells if a user is a recurring user
                    // // if you change logic here, also change it in login page
                    // const shouldMergeBasket = hasBasketItem && prevAuthType === 'guest'
                    // if (shouldMergeBasket) {
                    //     mergeBasket.mutate({
                    //         headers: {
                    //             // This is not required since the request has no body
                    //             // but CommerceAPI throws a '419 - Unsupported Media Type' error if this header is removed.
                    //             'Content-Type': 'application/json'
                    //         },
                    //         parameters: {
                    //             createDestinationBasket: true
                    //         }
                    //     })
                    // }

                    if (wishlistEnabled) {
                        mergeWishlistMutation(wishlistProductIds)
                    }

                    loginPromise.catch((data) => {
                        form.setError('global', {type: 'manual', message: ''})
                        data.response.json().then((error) => {
                            const message = /account.user.registration.activationmessage/i.test(
                                error.message
                            )
                                ? formatMessage({
                                      defaultMessage:
                                          'In order to login, you have to activate your account from the link we sent you in an email.',
                                      id: 'account-registration.msg.double_opt_in'
                                  })
                                : // upstream code checks only for unauthorized but our instance returns "Invalid Credentials"
                                /Unauthorized/i.test(error.message) ||
                                  /Invalid Credentials/i.test(error.message)
                                ? formatMessage({
                                      defaultMessage:
                                          'Incorrect username or password, please try again.',
                                      id: 'login_page.error.incorrect_username_or_password'
                                  })
                                : formatMessage(API_ERROR_MESSAGE)
                            form.setError('global', {type: 'manual', message})
                        })
                    })
                } else if (loginType === LOGIN_TYPES.PASSWORDLESS) {
                    setPasswordlessLoginEmail(data.email)
                    await handlePasswordlessLogin(data.email)
                }
            },
            register: async (data) => {
                const body = {
                    customer: {
                        firstName: data.firstName,
                        lastName: data.lastName,
                        email: data.email,
                        login: data.email,
                        preferredLocale: locale.id.replace('-', '_')
                    },
                    password: data.password
                }

                // intentionally not awaiting the promise here as we want to run in parallel with the newsletter subscription and tracking
                const registerPromise = !isDoubleOptInEnabled(data.email)
                    ? registerWithLogin.mutateAsync(body)
                    : registerWithoutLogin.mutateAsync({body})

                // intentionally not awaiting the promise here as we want to run in parallel with the registration and tracking
                const newsletterPromise = data.newsletter
                    ? subscribeToNewsletter({
                          body: JSON.stringify({
                              email: data.email,
                              firstName: data.firstName,
                              lastName: data.lastName
                          })
                      })
                    : true

                // intentionally not awaiting the promise here as we want to run in parallel with the registration and newsletter subscription
                const trackEventPromise = trackEvent({
                    event: 'account_create',
                    email: data.email
                })

                // wait for tracking and registration to complete, as otherwise the user might be redirected before the tracking is sent
                const [registerOutcome] = await Promise.allSettled([
                    registerPromise,
                    trackEventPromise,
                    newsletterPromise
                ])

                if (registerOutcome.status !== 'fulfilled') {
                    const message = await errorMsg(registerOutcome.reason)
                    form.setError('global', {type: 'manual', message})
                    return
                }

                if (!isDoubleOptInEnabled(data.email)) {
                    navigate(ACCOUNT_HREF)
                } else {
                    toast({
                        title: `${formatMessage({
                            defaultMessage:
                                'In order to login, you have to activate your account from the link we sent you in an email.',
                            id: 'account-registration.msg.double_opt_in'
                        })}`
                    })
                    props.onClose()
                }
            },
            password: async (data) => {
                if (!PWA_RESET_PASSWORD_ENABLED) {
                    try {
                        const body = {
                            identification: data.email,
                            type: 'email'
                        }
                        await passwordReset.mutateAsync({body})

                        // Execute action to be perfromed on successful passoword reset
                        await onPasswordResetSuccess?.()
                        submittedEmail.current = data.email
                    } catch (error) {
                        console.error('Error resetting password', error)
                        form.setError('global', {
                            type: 'manual',
                            message: formatMessage(API_ERROR_MESSAGE)
                        })
                    }
                } else {
                    try {
                        await getPasswordResetToken(data.email)
                        submittedEmail.current = data.email
                    } catch (e) {
                        const message = e.message?.startsWith('400 ')
                            ? formatMessage(FEATURE_UNAVAILABLE_ERROR_MESSAGE)
                            : formatMessage(API_ERROR_MESSAGE)
                        form.setError('global', {type: 'manual', message})
                    }
                }
            },
            email: async () => {
                await handlePasswordlessLogin(passwordlessLoginEmail)
            }
        }[currentView](data)
    })

    // Reset form and local state when opening the modal
    const onOpen = useEffectEvent(() => {
        setLoginType(LOGIN_TYPES.PASSWORD)
        setCurrentView(initialView)
        form.reset()
    })
    useEffect(() => {
        if (props.isOpen) {
            onOpen()
        }
    }, [props.isOpen, onOpen])

    // Auto-focus the first field in each form view
    const fieldsRef = form.control?.fieldsRef?.current
    useEffect(() => {
        if (fieldsRef) {
            const initialField = {
                [LOGIN_VIEW]: 'email',
                [REGISTER_VIEW]: 'firstName',
                [PASSWORD_VIEW]: 'email'
            }[currentView]
            fieldsRef?.[initialField]?.ref.focus()
        } else {
            // focus of close modal button
            closeButtonRef?.current?.focus()
        }
    }, [currentView, fieldsRef])

    // Clear form state when changing views
    const onViewChange = useEffectEvent(() => {
        form.reset()
    })
    useEffect(() => {
        onViewChange(currentView)
    }, [currentView, onViewChange])

    useEffect(() => {
        setPasswordlessLoginEmail(initialEmail)
    }, [initialEmail])

    const onCustomerChange = useEffectEvent((customer) => {
        // Lets determine if the user has either logged in, or registed.
        const loggingIn = currentView === LOGIN_VIEW
        const registering = currentView === REGISTER_VIEW
        const isNowRegistered = props.isOpen && isRegistered && (loggingIn || registering)
        // If the customer changed, but it's not because they logged in or registered. Do nothing.
        if (!isNowRegistered) {
            return
        }

        // We are done with the modal.
        props.onClose()

        if (isDoubleOptInEnabled(customer.email) && !customer.c_activated) {
            toast({
                title: formatMessage({
                    defaultMessage:
                        'In order to login, you have to activate your account from the link we sent you in an email.',
                    id: 'account-registration.msg.double_opt_in'
                })
            })
            return
        }

        // Show a toast only for those registered users returning to the site.
        if (loggingIn) {
            toast({
                title: `${formatMessage(
                    {
                        defaultMessage: 'Hello {name}, welcome!',
                        id: 'auth_modal.info.welcome_user'
                    },
                    {
                        name: customer.firstName
                    }
                )} ${formatMessage({
                    defaultMessage: "You're now signed in.",
                    id: 'auth_modal.description.now_signed_in'
                })}`
            })

            // Execute action to be performed on successful login
            onLoginSuccess()
        }

        if (registering) {
            // Execute action to be performed on successful registration
            onRegistrationSuccess()
        }

        // Show second toast for D2B customers
        if (isD2BCustomer && basket?.productItems?.length > 0) {
            toast({
                title: `${formatMessage({
                    defaultMessage:
                        'Please review your order summary as member discount has been applied.',
                    id: 'd2b.msg.review_order_total'
                })}`
            })
        }
    })
    useEffect(() => {
        if (customer.data && !customer.isLoading && !isBasketLoading) {
            onCustomerChange(customer.data)
        }
    }, [isBasketLoading, customer.data, customer.isLoading, onCustomerChange])

    const onBackToSignInClick = () =>
        initialView === PASSWORD_VIEW ? props.onClose() : setCurrentView(LOGIN_VIEW)

    const PasswordResetSuccess = () => (
        <Stack spacing={8}>
            <Text color="stokkePink.dark" textStyle="Regular 45XLarge">
                <FormattedMessage
                    defaultMessage={'Password Reset'}
                    id="auth_modal.password_reset_success.title.password_reset"
                />
            </Text>
            <Text textStyle="Regular Medium">
                <FormattedMessage
                    defaultMessage="If you have an account with this email address <b>{email}</b>, please check your inbox or spam folder. If you do not see a password reset email, please check if you have entered your email address correctly or <account>create an account</account>."
                    id="reset_password.info.receive_email"
                    values={{
                        email: submittedEmail.current,
                        b: (chunks) => <b>{chunks}</b>,
                        account: (chunks) => (
                            <Link href="/registration" size="md">
                                {chunks}
                            </Link>
                        )
                    }}
                />
            </Text>

            <Button onClick={onBackToSignInClick}>
                <FormattedMessage
                    defaultMessage="Back to Sign In"
                    id="auth_modal.password_reset_success.button.back_to_sign_in"
                />
            </Button>
        </Stack>
    )

    return (
        <Modal
            variant="auth"
            closeOnOverlayClick={false}
            returnFocusOnClose={false}
            data-testid="sf-auth-modal"
            {...props}
        >
            <ModalOverlay />
            <ModalContent
                aria-label={
                    currentView === PASSWORD_VIEW
                        ? formatMessage({
                              id: 'auth_modal.password_reset_success.title.password_reset',
                              defaultMessage: 'Password Reset'
                          })
                        : formatMessage({
                              id: 'login.page.title',
                              defaultMessage: 'Login into your Stokke® Account'
                          })
                }
            >
                <ModalCloseButton ref={closeButtonRef} />
                <ModalBody>
                    {!form.formState.isSubmitSuccessful && currentView === LOGIN_VIEW && (
                        <LoginForm
                            form={form}
                            submitForm={submitForm}
                            clickCreateAccount={() => setCurrentView(REGISTER_VIEW)}
                            handlePasswordlessLoginClick={() =>
                                setLoginType(LOGIN_TYPES.PASSWORDLESS)
                            }
                            handleForgotPasswordClick={() => setCurrentView(PASSWORD_VIEW)}
                            isPasswordlessEnabled={isPasswordlessEnabled}
                            isSocialEnabled={isSocialEnabled}
                            idps={idps}
                            setLoginType={setLoginType}
                        />
                    )}
                    {!form.formState.isSubmitSuccessful && currentView === REGISTER_VIEW && (
                        <RegisterForm
                            form={form}
                            submitForm={submitForm}
                            clickSignIn={onBackToSignInClick}
                        />
                    )}
                    {!form.formState.isSubmitSuccessful && currentView === PASSWORD_VIEW && (
                        <ResetPasswordForm
                            form={form}
                            submitForm={submitForm}
                            clickSignIn={onBackToSignInClick}
                        />
                    )}
                    {form.formState.isSubmitSuccessful && currentView === PASSWORD_VIEW && (
                        <PasswordResetSuccess />
                    )}
                    {currentView === EMAIL_VIEW && (
                        <PasswordlessEmailConfirmation
                            form={form}
                            submitForm={submitForm}
                            email={passwordlessLoginEmail}
                        />
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

AuthModal.propTypes = {
    initialView: PropTypes.oneOf([LOGIN_VIEW, REGISTER_VIEW, PASSWORD_VIEW, EMAIL_VIEW]),
    initialEmail: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
    onOpen: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onLoginSuccess: PropTypes.func,
    onRegistrationSuccess: PropTypes.func,
    onPasswordResetSuccess: PropTypes.func,
    isPasswordlessEnabled: PropTypes.bool,
    isSocialEnabled: PropTypes.bool,
    idps: PropTypes.arrayOf(PropTypes.string)
}

/**
 *
 * @param {('register'|'login'|'password'|'email')} initialView - the initial view for the modal
 * @returns {Object} - Object props to be spread on to the AuthModal component
 */
export const useAuthModal = (initialView = LOGIN_VIEW) => {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {passwordless = {}, social = {}} = getConfig().app.login || {}

    useLenisScrollLock(isOpen)

    return {
        initialView,
        isOpen,
        onOpen,
        onClose,
        isPasswordlessEnabled: !!passwordless?.enabled,
        isSocialEnabled: !!social?.enabled,
        idps: social?.idps
    }
}
